import { SCHEMA_EDITOR_ID } from "./stagingModel";

// A dropped source/sink's SCHEMA slot seeds asynchronously (variantSwitch:
// the stored schema named after the collection, else the introspected
// majority shape) — a stored-schema lookup bounded by its budget, then
// the engine's sampling on a cold cache. The slot shows "in progress"
// meanwhile, so the user knows a schema is on its way rather than that
// the drop staged none.

/** Whether the given SCHEMA slot row belongs to a stage whose seed is
 *  still resolving. Only the stage's own slot ("schema:editor:<stageId>")
 *  qualifies — the bottom blocks ("@" markers, the bare sink slot) never
 *  seed. */
export function slotSeeding(
  seeding: readonly string[],
  editorRowId: string,
): boolean {
  const prefix = `${SCHEMA_EDITOR_ID}:`;
  if (!editorRowId.startsWith(prefix)) return false;
  const stageId = editorRowId.slice(prefix.length);
  return (
    stageId !== "" && !stageId.startsWith("@") && seeding.includes(stageId)
  );
}

// Seeds for one stage can overlap — unstage and re-stage a sink while the
// first lookup is pending — and only the LATEST seed's completion may clear
// the stage's wait. The ledger hands each start a token; a completion
// clears the stage only when its token is still the current one.

/** Records a new seed for the stage; returns its token. */
export function beginSeed(ledger: Map<string, number>, stageId: string) {
  const token = (ledger.get(stageId) ?? 0) + 1;
  ledger.set(stageId, token);
  return token;
}

/** Records a seed's completion: true (and the stage cleared) only when it
 *  was the latest seed for the stage; a stale completion changes nothing. */
export function endSeed(
  ledger: Map<string, number>,
  stageId: string,
  token: number,
): boolean {
  if (ledger.get(stageId) !== token) return false;
  ledger.delete(stageId);
  return true;
}
